import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ShiftStatusPillGlobal from "@/components/ShiftStatusPillGlobal";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CaAuthProvider } from "@/context/CaAuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { EnvProvider, EnvBanner } from "@/components/EnvBanner";
import { ProtectedRoute } from "@/context/ProtectedRoute";
// Eager (needed on first paint or high-frequency)
import Home from "@/pages/Home";
import CsaHome from "@/pages/csa/CsaHome";
import CsaPortal from "@/pages/csa/CsaPortal";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import AuthCallback from "@/pages/auth/AuthCallback";
import HelloThere from "@/pages/HelloThere";
import ResetPassword from "@/pages/auth/ResetPassword";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import Profile from "@/pages/auth/Profile";
import DownloadPage from "@/pages/DownloadPage";
// Founder30 page retired — Feb 2026. Route redirects to home. Keeping file for reference.
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import AccountDeletion from "@/pages/legal/AccountDeletion";
import QuickEntryFAB from "@/components/QuickEntryFAB";
import { useNativeBridge } from "@/lib/native";

// Lazy: dealer + manager + admin — CSA users never load this JS. Cuts their
// cold-start bundle by ~60%.
const CustomerPortal = lazy(() => import("@/pages/customer/CustomerPortal"));
const Setup = lazy(() => import("@/pages/setup/Setup"));
const Cashbook = lazy(() => import("@/pages/manager/Cashbook"));
const VerifyHisab = lazy(() => import("@/pages/manager/VerifyHisab"));
const ShortageLedger = lazy(() => import("@/pages/manager/ShortageLedger"));
const Invites = lazy(() => import("@/pages/dealer/Invites"));
// Dashboard route removed in Brick 4 (N5) — its cards were merged into Home.
// Old /manager/dashboard bookmarks still land on Home via the redirect below.
const ManagerAttendance = lazy(() => import("@/pages/manager/Attendance"));
const DayClose = lazy(() => import("@/pages/manager/DayClose"));
const LiveBoard = lazy(() => import("@/pages/manager/LiveBoard"));
const MyAttendance = lazy(() => import("@/pages/manager/MyAttendance"));
const Payroll = lazy(() => import("@/pages/manager/Payroll"));
const EmailForwardingGuide = lazy(() => import("@/pages/dealer/EmailForwardingGuide"));
const AlertsHub = lazy(() => import("@/pages/dealer/AlertsHub"));
const DealerReferrals = lazy(() => import("@/pages/dealer/DealerReferrals"));
const AdminReferrals = lazy(() => import("@/pages/admin/AdminReferrals"));
const BrandKit = lazy(() => import("@/pages/BrandKit"));
const DensityCalculator = lazy(() => import("@/pages/tools/DensityCalculator"));
const DocumentsVault = lazy(() => import("@/pages/dealer/DocumentsVault"));
const HelpSupport = lazy(() => import("@/pages/support/HelpSupport"));
const TTRecord = lazy(() => import("@/pages/dealer/TTRecord"));
const CreditorInvoices = lazy(() => import("@/pages/dealer/CreditorInvoices"));
const TanksConfig = lazy(() => import("@/pages/dealer/TanksConfig"));
const NozzleVerify = lazy(() => import("@/pages/manager/NozzleVerify"));
const BankDeposits = lazy(() => import("@/pages/manager/BankDeposits"));
const CsaAttendance = lazy(() => import("@/pages/csa/CsaAttendance"));
const CsaLeaves = lazy(() => import("@/pages/csa/CsaLeaves"));
const CsaShortages = lazy(() => import("@/pages/csa/CsaShortages"));
// PumpRegisters module deprecated Feb 20 2026 — folded into TankManagement + LubeInventory.
const Ledgers = lazy(() => import("@/pages/manager/Ledgers"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminConsole = lazy(() => import("@/pages/admin/AdminConsole"));
const NozzleContinuity = lazy(() => import("@/pages/manager/NozzleContinuity"));
const DealerRates = lazy(() => import("@/pages/dealer/DealerRates"));
const LeaveRequests = lazy(() => import("@/pages/manager/LeaveRequests"));
const InviteAFriend = lazy(() => import("@/pages/InviteAFriend"));
const PromoVideo = lazy(() => import("@/pages/PromoVideo"));
const TourVideo = lazy(() => import("@/pages/TourVideo"));
const CreditCustomers = lazy(() => import("@/pages/dealer/CreditCustomers"));
const CreditCustomerLedger = lazy(() => import("@/pages/dealer/CreditCustomerLedger"));
const CreditHealthScanner = lazy(() => import("@/pages/dealer/CreditHealthScanner"));
const OpsConsole = lazy(() => import("@/pages/dealer/OpsConsole"));
const OnlinePayments = lazy(() => import("@/pages/dealer/OnlinePayments"));
const ExpenseAnalytics = lazy(() => import("@/pages/dealer/ExpenseAnalytics"));
const TasksPage = lazy(() => import("@/pages/tasks/TasksPage"));
const DemoLandingPage = lazy(() => import("@/pages/DemoLandingPage"));
const LubeInventory = lazy(() => import("@/pages/dealer/LubeInventory"));
const TankManagement = lazy(() => import("@/pages/dealer/TankManagement"));
const StockRegister = lazy(() => import("@/pages/dealer/StockRegister"));
const DensityRegister = lazy(() => import("@/pages/dealer/DensityRegister"));
const WalkInOnboard = lazy(() => import("@/pages/walkin/WalkInOnboard"));
const CaAddon = lazy(() => import("@/pages/dealer/CaAddon"));
const CaAuth = lazy(() => import("@/pages/ca/CaAuth"));
const CaPortal = lazy(() => import("@/pages/ca/CaPortal"));
const DispensersSetup = lazy(() => import("@/pages/dealer/DispensersSetup"));
const DataOps = lazy(() => import("@/pages/dealer/DataOps"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const ShiftOverrides = lazy(() => import("@/pages/manager/ShiftOverrides"));

function LazyFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
      <div className="animate-pulse">Loading…</div>
    </div>
  );
}

/**
 * Redirect helper that preserves the current URL's `?query` and `#hash`.
 * `<Navigate to="/x/c1" />` drops them by default — that breaks flows like
 * `/csa/shift?fix=<hisab_id>` where downstream code reads
 * `window.location.search` to decide which HISAB to load.
 */
function QueryNav({ to }) {
  const loc = useLocation();
  return <Navigate to={{ pathname: to, search: loc.search, hash: loc.hash }} replace />;
}

function AppInner() {
  useNativeBridge();
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <EnvProvider>
          <AuthProvider>
            <EnvBanner />
            <AppInner />
            <ShiftStatusPillGlobal />
            <Suspense fallback={<LazyFallback />}>
            <Routes>
            {/* Public / auth entries — stay descriptive for SEO + shareability */}
            <Route path="/login" element={<Login />} />
            {/* Demo landing — moved off /demo (public) to a non-guessable URL
                so external visitors can't stumble into it. Share this URL
                internally / with QA / on the pitch deck. Rename the slug
                below if it ever leaks; the DemoLandingPage component is
                self-contained. */}
            <Route path="/preview-e3f9a2" element={<DemoLandingPage />} />
            {/* Redirect any bookmarks that still hit the old /demo URL back
                to /login so we don't 404 people who came from an old QR. */}
            <Route path="/demo" element={<Navigate to="/login" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite" element={<AcceptInvite />} />
            <Route path="/customer" element={<CustomerPortal />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/brand-kit" element={<BrandKit />} />
            <Route path="/tools/density" element={<ProtectedRoute roles={["DEALER", "MANAGER", "CSA"]}><DensityCalculator /></ProtectedRoute>} />
            <Route path="/dealer/documents" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><DocumentsVault /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpSupport /></ProtectedRoute>} />
            <Route path="/dealer/tt" element={<ProtectedRoute roles={["DEALER", "MANAGER", "CSA"]}><TTRecord /></ProtectedRoute>} />
            <Route path="/dealer/invoices" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><CreditorInvoices /></ProtectedRoute>} />
            <Route path="/promo" element={<PromoVideo />} />
            <Route path="/tour" element={<TourVideo />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />
            <Route path="/delete-account" element={<AccountDeletion />} />
            <Route path="/hello" element={<HelloThere />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/auth/google" element={<AuthCallback />} />
            <Route path="/auth-callback" element={<AuthCallback />} />

            {/* CA (Chartered Accountant) — separate auth realm */}
            <Route path="/ca/login" element={<CaAuthProvider><CaAuth mode="login" /></CaAuthProvider>} />
            <Route path="/ca/accept" element={<CaAuthProvider><CaAuth mode="accept" /></CaAuthProvider>} />
            <Route path="/ca" element={<CaAuthProvider><CaPortal /></CaAuthProvider>} />

            {/* ================================================================
                Masked internal routes — real pages live at /x/{code}.
                Any legacy descriptive path just redirects here.
               ================================================================ */}
            <Route path="/x/dh" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/x/c0" element={<ProtectedRoute roles={["CSA"]}><CsaHome /></ProtectedRoute>} />
            <Route path="/x/c1" element={<ProtectedRoute roles={["CSA"]}><CsaPortal /></ProtectedRoute>} />
            <Route path="/x/c2" element={<ProtectedRoute roles={["CSA"]}><CsaAttendance /></ProtectedRoute>} />
            <Route path="/x/c3" element={<ProtectedRoute roles={["CSA"]}><CsaLeaves /></ProtectedRoute>} />
            <Route path="/x/c4" element={<ProtectedRoute roles={["CSA"]}><CsaShortages /></ProtectedRoute>} />
            <Route path="/x/d1" element={<Navigate to="/dealer/tank-management" replace />} />
            <Route path="/x/m1" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><Cashbook /></ProtectedRoute>} />
            <Route path="/x/m2" element={<Navigate to="/" replace />} />
            <Route path="/x/m3" element={<QueryNav to="/x/m5" />} />
            <Route path="/x/m4" element={<Navigate to="/x/m5" replace />} />
            <Route path="/x/m5" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><DayClose /></ProtectedRoute>} />
            <Route path="/x/m6" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><LiveBoard /></ProtectedRoute>} />
            <Route path="/x/m7" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><ShiftOverrides /></ProtectedRoute>} />
            <Route path="/x/m8" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><ManagerAttendance /></ProtectedRoute>} />
            <Route path="/x/m9" element={<Navigate to="/x/m1" replace />} />
            <Route path="/x/ma" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><NozzleVerify /></ProtectedRoute>} />
            <Route path="/x/mb" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><BankDeposits /></ProtectedRoute>} />
            <Route path="/x/mc" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><Ledgers /></ProtectedRoute>} />
            <Route path="/x/md" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><ShortageLedger /></ProtectedRoute>} />
            <Route path="/x/me" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><MyAttendance /></ProtectedRoute>} />
            <Route path="/manager/payroll" element={<ProtectedRoute roles={["MANAGER", "DEALER"]}><Payroll /></ProtectedRoute>} />
            <Route path="/x/d8" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><EmailForwardingGuide /></ProtectedRoute>} />
            <Route path="/x/d9" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><TanksConfig /></ProtectedRoute>} />
            <Route path="/x/da" element={<ProtectedRoute roles={["DEALER"]}><AlertsHub /></ProtectedRoute>} />
            <Route path="/x/d2" element={<ProtectedRoute roles={["DEALER"]}><Invites /></ProtectedRoute>} />
            <Route path="/x/d3" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><CreditCustomers /></ProtectedRoute>} />
            <Route path="/dealer/credit-customers/:id/ledger" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><CreditCustomerLedger /></ProtectedRoute>} />
            <Route path="/dealer/credit-health" element={<ProtectedRoute roles={["DEALER"]}><CreditHealthScanner /></ProtectedRoute>} />
            <Route path="/dealer/ops-console" element={<ProtectedRoute roles={["DEALER"]}><OpsConsole /></ProtectedRoute>} />
            <Route path="/dealer/online-payments" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><OnlinePayments /></ProtectedRoute>} />
            <Route path="/dealer/expenses" element={<Navigate to="/dealer/expense-analytics" replace />} />
            <Route path="/dealer/expense-analytics" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><ExpenseAnalytics /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute roles={["DEALER", "MANAGER", "CSA"]}><TasksPage /></ProtectedRoute>} />
            <Route path="/dealer/lube" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><LubeInventory /></ProtectedRoute>} />
            <Route path="/dealer/tank-management" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><TankManagement /></ProtectedRoute>} />
            <Route path="/dealer/stock-register" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><StockRegister /></ProtectedRoute>} />
            <Route path="/dealer/density-register" element={<ProtectedRoute roles={["DEALER", "MANAGER"]}><DensityRegister /></ProtectedRoute>} />
            <Route path="/dealer/inventory" element={<Navigate to="/dealer/tank-management" replace />} />
            <Route path="/walkin/:pumpId" element={<WalkInOnboard />} />
            <Route path="/x/d4" element={<ProtectedRoute roles={["DEALER"]}><DispensersSetup /></ProtectedRoute>} />
            <Route path="/x/d5" element={<ProtectedRoute roles={["DEALER"]}><DataOps /></ProtectedRoute>} />
            <Route path="/x/d6" element={<ProtectedRoute roles={["DEALER","MANAGER"]}><DealerRates /></ProtectedRoute>} />
            <Route path="/x/d7" element={<ProtectedRoute roles={["DEALER"]}><CaAddon /></ProtectedRoute>} />
            <Route path="/x/r2" element={<ProtectedRoute roles={["MANAGER","DEALER"]}><NozzleContinuity /></ProtectedRoute>} />
            <Route path="/x/r3" element={<ProtectedRoute><LeaveRequests /></ProtectedRoute>} />
            <Route path="/x/s0" element={<ProtectedRoute roles={["DEALER"]} requireSetup={false}><Setup /></ProtectedRoute>} />
            <Route path="/x/p0" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/x/iaf" element={<ProtectedRoute><InviteAFriend /></ProtectedRoute>} />
            <Route path="/x/ac" element={<AdminConsole />} />
            {/* Brick 6 N16 — Referral pages (dealer history + admin review) */}
            <Route path="/dealer/referrals" element={<ProtectedRoute roles={["DEALER"]}><DealerReferrals /></ProtectedRoute>} />
            <Route path="/admin/referrals" element={<AdminReferrals />} />

            {/* ================================================================
                Legacy descriptive paths → auto-redirect to masked route so
                every existing <Link to="..."> in the codebase keeps working
                without a source change. `replace` prevents polluting history.

                IMPORTANT: <Navigate to="/x/xx"> literally uses that path and
                drops any `?query`. For flows that rely on query params (e.g.
                `/csa/shift?fix=<hisab_id>` → axios interceptor picks up the
                fix id) we use <QueryNav> so search/hash are preserved.
               ================================================================ */}
            <Route path="/" element={<Navigate to="/x/dh" replace />} />
            <Route path="/csa" element={<QueryNav to="/x/c0" />} />
            <Route path="/csa/shift" element={<QueryNav to="/x/c1" />} />
            <Route path="/csa/attendance" element={<QueryNav to="/x/c2" />} />
            <Route path="/csa/leaves" element={<QueryNav to="/x/c3" />} />
            <Route path="/csa/shortages" element={<QueryNav to="/x/c4" />} />
            <Route path="/dealer/registers" element={<Navigate to="/dealer/tank-management" replace />} />
            <Route path="/manager/cashbook" element={<Navigate to="/x/m1" replace />} />
            <Route path="/manager/dashboard" element={<Navigate to="/" replace />} />
            {/* Testing Notes 16 · Unified "Hisab Center" — Verify HISABs +
                Shift-wise Report + Cycle Summary all collapsed into
                /manager/day-close (masked /x/m5). Old bookmarks redirect. */}
            <Route path="/hisab" element={<QueryNav to="/x/m5" />} />
            <Route path="/manager/verify" element={<QueryNav to="/x/m5" />} />
            <Route path="/manager/hisabs" element={<Navigate to="/x/m5" replace />} />
            <Route path="/manager/day-close" element={<QueryNav to="/x/m5" />} />
            <Route path="/manager/live-board" element={<Navigate to="/x/m6" replace />} />
            <Route path="/manager/shift-overrides" element={<Navigate to="/x/m7" replace />} />
            <Route path="/manager/attendance" element={<Navigate to="/x/m8" replace />} />
            <Route path="/manager/cash-collections" element={<Navigate to="/manager/cashbook" replace />} />
            <Route path="/manager/nozzle-verify" element={<Navigate to="/x/ma" replace />} />
            <Route path="/manager/bank-deposits" element={<Navigate to="/x/mb" replace />} />
            <Route path="/manager/ledgers" element={<Navigate to="/x/mc" replace />} />
            <Route path="/manager/shortage" element={<Navigate to="/x/md" replace />} />
            <Route path="/manager/my-attendance" element={<Navigate to="/x/me" replace />} />
            <Route path="/dealer/email-forwarding" element={<Navigate to="/x/d8" replace />} />
            <Route path="/dealer/tanks" element={<Navigate to="/x/d9" replace />} />
            <Route path="/dealer/alerts" element={<Navigate to="/x/da" replace />} />
            <Route path="/dealer/invites" element={<Navigate to="/x/d2" replace />} />
            <Route path="/dealer/credit-customers" element={<Navigate to="/x/d3" replace />} />
            <Route path="/dealer/dispensers" element={<Navigate to="/x/d4" replace />} />
            <Route path="/dealer/data-ops" element={<Navigate to="/x/d5" replace />} />
            <Route path="/dealer/rates" element={<Navigate to="/x/d6" replace />} />
            <Route path="/dealer/ca-addon" element={<Navigate to="/x/d7" replace />} />
            <Route path="/nozzles/continuity" element={<Navigate to="/x/r2" replace />} />
            <Route path="/leave-requests" element={<Navigate to="/x/r3" replace />} />
            <Route path="/setup" element={<Navigate to="/x/s0" replace />} />
            <Route path="/profile" element={<Navigate to="/x/p0" replace />} />
            <Route path="/invite-a-friend" element={<Navigate to="/x/iaf" replace />} />
            <Route path="/admin" element={<Navigate to="/x/ac" replace />} />

            {/* Founder-30 program retired — routes now redirect home. */}
            <Route path="/founder30" element={<Navigate to="/x/dh" replace />} />
            <Route path="/founder50" element={<Navigate to="/x/dh" replace />} />
          </Routes>
          </Suspense>
          <QuickEntryFAB />
          </AuthProvider>
          </EnvProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
